import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, useInView } from "framer-motion";
import { Loader2, Zap, TrendingDown, Sun } from "lucide-react";
import { toast } from "sonner";
import { FadeUp } from "./anim";
import { API } from "./site-data";

const CountUp = ({ value, prefix = "", suffix = "", decimals = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const dur = 1200;
    const tick = (now) => {
      const p = Math.min((now - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(value * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);

  const formatted = display.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return (
    <span ref={ref}>
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
};

const Metric = ({ label, value, prefix, suffix, decimals, highlight, testid }) => (
  <div
    data-testid={testid}
    className={`rounded-2xl border p-6 ${
      highlight
        ? "border-sg-primary/40 bg-sg-primary/10"
        : "border-white/5 bg-sg-bg"
    }`}
  >
    <div
      className={`font-display text-3xl sm:text-4xl tracking-tight ${
        highlight ? "text-sg-primary" : "text-sg-fg"
      }`}
    >
      <CountUp value={value} prefix={prefix} suffix={suffix} decimals={decimals} />
    </div>
    <div className="mt-2 text-xs uppercase tracking-[0.15em] text-sg-muted">
      {label}
    </div>
  </div>
);

const EstimateEmailForm = ({ result, zip, rate }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    if (!/.+@.+\..+/.test(email)) {
      toast.error("Please enter a valid email.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/estimate-lead`, {
        name,
        email,
        zip_code: zip.trim(),
        rate: parseFloat(rate) || 0.16,
        daily_kwh: result.daily_kwh,
        monthly_kwh: result.monthly_kwh,
        annual_kwh: result.annual_kwh,
        annual_savings: result.annual_savings,
        ten_year_savings: result.ten_year_savings,
      });
      setSent(true);
      toast.success("Sent! Check your inbox for your estimate.");
    } catch (err) {
      toast.error("Couldn't send that. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-lg bg-sg-bg border border-sg-border px-4 py-3 text-sm text-sg-fg placeholder:text-sg-muted/60 focus:outline-none focus:border-sg-primary transition-colors duration-300";

  if (sent) {
    return (
      <div
        className="rounded-2xl border border-sg-primary/40 bg-sg-primary/10 p-6 text-sm text-sg-fg"
        data-testid="estimate-sent"
      >
        Your estimate is on its way to <b>{email}</b>. We'll follow up personally
        if you'd like to move forward.
      </div>
    );
  }

  return (
    <form
      onSubmit={send}
      className="rounded-2xl border border-sg-border bg-sg-card p-6"
      data-testid="estimate-email-form"
    >
      <p className="text-sm text-sg-fg mb-4">
        Want a copy? We'll email this estimate to you.
      </p>
      <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
        <input
          className={inputCls}
          placeholder="Name (optional)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="estimate-name"
        />
        <input
          className={inputCls}
          type="email"
          placeholder="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="estimate-email"
        />
        <button
          type="submit"
          disabled={sending}
          data-testid="estimate-send-btn"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-sg-primary px-6 py-3 text-sm font-medium text-sg-bg hover:bg-sg-fg transition-colors duration-300 disabled:opacity-60 whitespace-nowrap"
        >
          {sending && <Loader2 size={15} className="animate-spin" />}
          Email My Estimate
        </button>
      </div>
    </form>
  );
};

export const Calculator = () => {
  const [zip, setZip] = useState("");
  const [rate, setRate] = useState("0.16");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const calc = async (e) => {
    e.preventDefault();
    if (!/^\d{3,}/.test(zip.trim())) {
      toast.error("Please enter a valid US ZIP code.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/solar-estimate`, {
        zip_code: zip.trim(),
        rate: parseFloat(rate) || 0.16,
      });
      setResult(data);
    } catch (err) {
      toast.error("Couldn't calculate an estimate. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-lg bg-sg-bg border border-sg-border px-4 py-3 text-sm text-sg-fg placeholder:text-sg-muted/60 focus:outline-none focus:border-sg-primary transition-colors duration-300";

  return (
    <section
      id="savings"
      className="relative bg-sg-bg py-24 sm:py-32 border-t border-sg-border/50"
      data-testid="savings-section"
    >
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <FadeUp className="max-w-2xl mb-14">
          <span className="text-xs uppercase tracking-[0.25em] text-sg-primary">
            Sunlight → Solar Power → Home Energy → Savings
          </span>
          <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-sg-fg">
            See what your Solar Garden could save.
          </h2>
          <p className="mt-5 text-base text-sg-muted font-light max-w-xl">
            Enter your ZIP code and electric rate for a quick estimate of the
            energy your Solar Garden could produce — and what that could mean
            for your electric bill.
          </p>
        </FadeUp>

        <div className="grid gap-6 lg:gap-10 lg:grid-cols-12 items-start">
          <FadeUp className="lg:col-span-4">
            <form
              onSubmit={calc}
              className="rounded-3xl border border-sg-border bg-sg-card p-7 sm:p-8"
            >
              <label className="text-xs uppercase tracking-[0.15em] text-sg-muted">
                ZIP code
              </label>
              <input
                className={`${inputCls} mt-2`}
                placeholder="e.g. 90210"
                inputMode="numeric"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                data-testid="calc-zip"
              />
              <label className="mt-5 block text-xs uppercase tracking-[0.15em] text-sg-muted">
                Electric rate ($/kWh)
              </label>
              <input
                className={`${inputCls} mt-2`}
                placeholder="0.16"
                inputMode="decimal"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                data-testid="calc-rate"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="calc-submit-btn"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-sg-primary px-6 py-3.5 text-sm font-medium text-sg-bg hover:bg-sg-fg transition-colors duration-300 disabled:opacity-60"
              >
                {loading && <Loader2 size={15} className="animate-spin" />}
                Estimate My Savings
              </button>
              <p className="mt-4 text-[11px] leading-relaxed text-sg-muted">
                Estimates only, based on an 880W solar canopy (2×440W) with a
                900W microinverter and regional sun data. Actual production and
                savings depend on installation, shading, usage, and local
                utility rules.
              </p>
            </form>
          </FadeUp>

          <div className="lg:col-span-8">
            {result ? (
              <div className="space-y-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="grid gap-4 sm:grid-cols-2"
                data-testid="calc-results"
              >
                <Metric
                  testid="metric-daily"
                  label="Est. daily production"
                  value={result.daily_kwh}
                  suffix=" kWh"
                  decimals={1}
                />
                <Metric
                  testid="metric-monthly"
                  label="Est. monthly production"
                  value={result.monthly_kwh}
                  suffix=" kWh"
                  decimals={0}
                />
                <Metric
                  testid="metric-annual"
                  label="Est. annual production"
                  value={result.annual_kwh}
                  suffix=" kWh"
                  decimals={0}
                />
                <Metric
                  testid="metric-annual-savings"
                  label="Est. annual savings"
                  value={result.annual_savings}
                  prefix="$"
                  decimals={0}
                  highlight
                />
                <div className="sm:col-span-2">
                  <Metric
                    testid="metric-tenyear"
                    label="Est. 10-year savings"
                    value={result.ten_year_savings}
                    prefix="$"
                    decimals={0}
                    highlight
                  />
                </div>
              </motion.div>
              <EstimateEmailForm result={result} zip={zip} rate={rate} />
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 h-full">
                {[
                  { icon: Sun, t: "Daily & monthly production" },
                  { icon: Zap, t: "Annual energy generated" },
                  { icon: TrendingDown, t: "Annual bill offset" },
                  { icon: TrendingDown, t: "10-year savings outlook" },
                ].map((p, i) => (
                  <div
                    key={i}
                    className="rounded-2xl border border-dashed border-sg-border bg-sg-card/40 p-6 flex items-center gap-3 text-sm text-sg-muted"
                  >
                    <span className="grid place-items-center w-9 h-9 rounded-full bg-sg-primary/12 text-sg-primary shrink-0">
                      <p.icon size={16} />
                    </span>
                    {p.t}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
